/**
 * This is the entry point for Feature Hub App integration
 */

import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { Logger } from '@feature-hub/logger';
import type { ReactFeatureApp } from '@feature-hub/react';
import type {
  TrackingService,
  ContentService,
  RenderModeService,
  AudiFootnoteReferenceServiceInterface,
  AudiFootnoteRefernceServiceScopeManagerInterface,
  VueFormatterServiceInterface,
  LocaleService,
  GfaServiceConfigProviderV1,
  SerializedStateManagerV1,
  AsyncSsrManagerV1,
} from './types/feature-service-types';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import { FootnoteContextProvider } from '@oneaudi/feature-app-utils';
import React from 'react';

import App from './FeatureApp';
import { ContentContextProvider } from './context/ContentContext';
import { fetchCaeEnforcement } from './marketCompliance';
import { TrackingServiceContextProvider } from './context/trackingServiceContext';
import { runSsrWltpPrecheck } from './utils/runSsrWltpPrecheck';
import { getDesignLibrary } from './utils/alphaHelper';

export interface Dependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'audi-content-service': ContentService;
  readonly 'dbad:audi-vue-formatter-service': VueFormatterServiceInterface;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterface;
  readonly 'locale-service': LocaleService;
  readonly 'gfa:service-config-provider'?: GfaServiceConfigProviderV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1;
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1 | undefined;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, Dependencies> = {
  dependencies: {
    featureServices: {
      'dbad:audi-vue-formatter-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'locale-service': '^1.0.0',
    },
    externals: {
      react: '^16.13.1 || ^17.0.2 || ^18.0.0',
      'react-dom': '^16.13.1 || ^17.0.2 || ^18.0.0',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },

  optionalDependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
      'audi-render-mode-service': '^1.0.0',
      'gfa:service-config-provider': '^1.0.0',
    },
  },

  create: ({ featureServices }: FeatureAppEnvironment<Dependencies, void>) => {
    const ssrState: { initialWltpCompliance?: boolean[]; isMarketComplianceEnabled?: boolean } = {};
    const gfaServiceConfigProvider = featureServices['gfa:service-config-provider'];
    const serviceAsyncSsrManager = featureServices['s2:async-ssr-manager'];
    const serviceSerializedStateManager = featureServices['s2:serialized-state-manager'];
    const logger = featureServices['s2:logger'];
    logger?.info(`Feature App ${__FEATURE_APP_NAME__} created.`);
    const contentService = featureServices['audi-content-service'];
    const vueFormatterService = featureServices['dbad:audi-vue-formatter-service'];
    const referenceServiceScopeManager: AudiFootnoteRefernceServiceScopeManagerInterface =
      featureServices['audi-footnote-reference-service'];
    const footnoteReferenceService: AudiFootnoteReferenceServiceInterface =
      referenceServiceScopeManager.getDefaultScopeRefService();
    const localeService = featureServices['locale-service'];

    const trackingService = featureServices['audi-tracking-service'] as TrackingService;
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    const renderModeService = featureServices['audi-render-mode-service'] as RenderModeService;

    // DEBUG: Enable compliance for local testing if flag is present in search params
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('enableWltpCompliance') === 'true') {
        ssrState.isMarketComplianceEnabled = true;
      }
    }

    // CSR: rehydrate compliance state from SSR serialized output
    if (!serviceAsyncSsrManager) {
      try {
        const raw = serviceSerializedStateManager?.getSerializedState();
        if (raw) {
          const parsed = JSON.parse(raw) as {
            content?: Record<string, unknown>;
            isMarketComplianceEnabled?: boolean;
            initialWltpCompliance?: boolean[];
          };
          ssrState.isMarketComplianceEnabled = parsed.isMarketComplianceEnabled;
          ssrState.initialWltpCompliance = parsed.initialWltpCompliance;
        }
      } catch {
        // fail-open: if deserialization fails, CSR will run its own compliance check
      }
    }

    if (serviceAsyncSsrManager) {
      // This 3 step process inspired by this link:
      // https://github.com/oneaudi/i18n-context?tab=readme-ov-file#featureapp-definition
      // Was getting TS errors by adding `async` within `scheduleRerender`
      const waitForThese = async () => {
        const { caeDataRequired } = await fetchCaeEnforcement({ gfaServiceConfigProvider });
        ssrState.isMarketComplianceEnabled = caeDataRequired;
        const content = (await contentService?.getContent()) as Record<string, unknown>;
        ssrState.initialWltpCompliance = await runSsrWltpPrecheck({
          isMarketComplianceEnabled: caeDataRequired,
          content,
          vueFormatterService,
          localeService,
          logger,
        });
        serviceSerializedStateManager?.register(() =>
          JSON.stringify({
            content,
            isMarketComplianceEnabled: ssrState.isMarketComplianceEnabled,
            initialWltpCompliance: ssrState.initialWltpCompliance,
          }),
        );
      };
      serviceAsyncSsrManager.scheduleRerender(waitForThese());
    }

    return {
      render: () => {
        return (
          <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
            <ContentContextProvider
              contentService={contentService}
              vueFormatterService={vueFormatterService}
              logger={logger}
              localeService={localeService}
            >
              <TrackingServiceContextProvider trackingService={trackingService}>
                <div data-fefa-custom-id={footnoteReferenceService?.getConsumerId()}>
                  <UniversalEditorProvider
                    contentService={contentService}
                    renderModeService={renderModeService}
                  >
                    <App
                      isMarketComplianceEnabled={ssrState.isMarketComplianceEnabled}
                      initialWltpCompliance={ssrState.initialWltpCompliance}
                    />
                  </UniversalEditorProvider>
                </div>
              </TrackingServiceContextProvider>
            </ContentContextProvider>
          </FootnoteContextProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
